/**
 * EmbedSessionBanner — session status for the embedded editor.
 *
 * An embed's token is short-lived and refreshed by the host. When that
 * lapses, every API call 401s; without this the editor looks perfectly
 * healthy while autosave quietly fails, which is the worst version of the
 * failure. So: say something.
 *
 * Renders nothing on a normal (non-embedded) page, and nothing while the
 * session is healthy.
 */
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';

import {
  EmbedSessionState,
  getEmbedSessionState,
  isEmbedSession,
} from '@/app/bootstrap/embed-session/embed-session';

const STYLE_ID = '__tile_embed_session_styles__';
const STYLES = `
@keyframes tile-embed-fade-in {
  from { transform: translate(-50%, -8px); opacity: 0; }
  to   { transform: translate(-50%, 0);    opacity: 1; }
}
.tile-embed-status {
  position: fixed;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10001;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 16px;
  border-radius: 999px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-size: 12.5px;
  font-weight: 500;
  animation: tile-embed-fade-in 200ms cubic-bezier(0.22, 1, 0.36, 1);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.05);
}
.tile-embed-status--refreshing {
  background: #ffffff;
  color: #444;
}
.tile-embed-status--expired {
  background: #fff8f0;
  color: #8a4b00;
  box-shadow: 0 6px 20px rgba(220, 120, 0, 0.16), 0 0 0 1px rgba(220, 120, 0, 0.22);
}
.tile-embed-spinner {
  width: 13px;
  height: 13px;
  border: 2px solid rgba(0, 0, 0, 0.12);
  border-top-color: #666;
  border-radius: 999px;
  animation: tile-embed-spin 700ms linear infinite;
}
@keyframes tile-embed-spin { to { transform: rotate(360deg); } }
`;

function ensureStyles(): void {
  if (typeof document === 'undefined' || document.getElementById(STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = STYLE_ID;
  el.textContent = STYLES;
  document.head.appendChild(el);
}

export default function EmbedSessionBanner() {
  const [state, setState] = useState<EmbedSessionState>(getEmbedSessionState());

  useEffect(() => {
    if (!isEmbedSession()) return;
    ensureStyles();
    const onChange = (event: Event) => {
      const detail = (event as CustomEvent<{ state: EmbedSessionState }>).detail;
      if (detail?.state) setState(detail.state);
    };
    window.addEventListener('tile:embed-session', onChange);
    return () => window.removeEventListener('tile:embed-session', onChange);
  }, []);

  if (!isEmbedSession() || state === 'active' || typeof document === 'undefined') return null;

  return ReactDOM.createPortal(
    <div className={`tile-embed-status tile-embed-status--${state}`} role="status">
      {state === 'refreshing' ? (
        <>
          <span className="tile-embed-spinner" aria-hidden />
          <span>Reconnecting your session…</span>
        </>
      ) : (
        <span>
          Session expired. Reload this page from {/* the host app, not Tile */}
          the dashboard to keep editing — recent changes may not have saved.
        </span>
      )}
    </div>,
    document.body,
  );
}
